import React from 'react';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  IconButton,
  Button,
  useColorModeValue,
  Switch,
  Badge,
  Input,
  Skeleton,
  Link,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  Divider,
  ModalFooter,
} from '@chakra-ui/react';
import {
  EditIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DeleteIcon,
  ExternalLinkIcon,
  AttachmentIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  QuestionIcon,
} from '@chakra-ui/icons';
import { FiGrid, FiHelpCircle, FiList } from 'react-icons/fi';
import { motion } from 'framer-motion';
import Card from 'components/card/Card';
import PropTypes from 'prop-types';
import { downloadMasterExcel } from 'api/auth.api';
import downloadFile from 'constants/helper/downloadFile';
import StyledDropdown from 'components/StyledDropdown';
import ReadMoreCell from './ReadMoreCell';
import TableDateFilter from './TableDateFilter';
import baseUrl from 'Base_Url/baseUrl';
import { keyframes } from '@emotion/react';

const MotionTr = motion(Tr);

function ReusableTable({
  data,
  columns,
  page,
  totalPages,
  loading,
  onRowClick,
  onEdit,
  onDelete,
  onToggleActive,
  onPageChange,
  showTopAction = false,
  topAction = null,

  showUpload = false,
  onUpload = null,

  showCommonFilter = false,
  commonFilterOptions = [],
  commonFilterValue = '',
  onCommonFilterChange = null,

  showFilter = false,
  filterOptions = [],
  filterValue = '',

  showViewToggle = true,
  showSearch = false,
  onSearch = null,
  showAddBtn = true,

  showDateFilter = false,
  dateFilterConfig = undefined,
  dateFilterValues = undefined,
  onDateFilterChange = null,
  sortKey = '',
  sortOrder = '', // '', 'asc', 'desc'
  onSort = null,
  errorLogData = null,
}) {
  const ROWS_PER_PAGE = 10;
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const headerBg = useColorModeValue('blue.50', 'gray.800');
  const headerText = useColorModeValue('blue.700', 'blue.200');
  const zebraBg = useColorModeValue('gray.50', 'gray.800');
  const inactiveBg = useColorModeValue('red.50', 'red.900');
  const bgCard = useColorModeValue('white', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const noDataTextColor = useColorModeValue('gray.500', 'gray.300');
  const paginationTextColor = useColorModeValue('gray.600', 'gray.300');

  const [viewMode, setViewMode] = React.useState('table');

  const [selectedFile, setSelectedFile] = React.useState(null);
  const fileInputRef = React.useRef(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const blink = keyframes`
  0% { background-color: #FED7D7; }   /* red.100 */
  50% { background-color: #FEB2B2; }  /* red.200 */
  100% { background-color: #FED7D7; }
`;
  return (
    <Box>
      {/* TOP ACTION */}
      {showTopAction && topAction && (
        <Flex align="center" mb={4}>
          <Text fontSize="22px" fontWeight="bold" color={textColor}>
            {topAction.title}
          </Text>
          {showAddBtn && (
            <Button
              leftIcon={topAction.icon}
              colorScheme="blue"
              onClick={topAction.onClick}
              ml="auto"
            >
              {topAction.label}
            </Button>
          )}
        </Flex>
      )}

      <Flex
        mb={4}
        gap={{ base: 3, md: 4 }}
        direction="row"
        flexWrap="wrap"
        align="center"
        justify="space-between"
      >
        <Flex
          gap={2}
          align="center"
          flexWrap="wrap"
          flex={{ base: '1 1 100%', md: 'auto' }}
        >
          {/* SEARCH */}
          {showSearch && (
            <Box w={{ base: '100%', md: '260px', lg: '300px' }}>
              <Input
                placeholder="Search..."
                onChange={(e) => onSearch?.(e.target.value)}
                bg={bgCard}
                color={textColor}
                borderColor={borderColor}
                borderRadius="md"
                _hover={{
                  borderColor: 'blue.400',
                }}
                _focus={{
                  borderColor: 'blue.500',
                  boxShadow: '0 0 0 1px #3182ce',
                }}
              />
            </Box>
          )}

          {/* SELECT */}
          {showCommonFilter && (
            <Box w={{ base: '100%', md: '200px', lg: '230px' }}>
              <StyledDropdown
                options={commonFilterOptions}
                value={commonFilterValue || null}
                onChange={onCommonFilterChange}
                placeholder="All"
                bg={bgCard}
                color={textColor}
                borderColor={borderColor}
                isClearable
              />
            </Box>
          )}
        </Flex>

        {(showUpload || showFilter) && (
          <Flex
            gap={3}
            align={{ base: 'stretch', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            w={{ base: '100%', md: 'auto' }}
          >
            {/* UPLOAD */}
            {showUpload && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  hidden
                  accept=".xls,.xlsx"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    const isExcel =
                      file.type ===
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      file.type === 'application/vnd.ms-excel';

                    if (!isExcel) {
                      alert('Please upload only Excel files (.xls, .xlsx)');
                      e.target.value = '';
                      return;
                    }

                    setSelectedFile(file);
                  }}
                />

                {!selectedFile ? (
                  <Button
                    w={{ base: '100%', md: 'auto' }}
                    size={{ base: 'sm', md: 'md' }}
                    colorScheme="teal"
                    onClick={() => fileInputRef.current.click()}
                  >
                    Upload File
                  </Button>
                ) : (
                  <Flex gap={2} align="center">
                    {selectedFile && (
                      <Text color="blue.500" fontWeight="bold" fontSize="sm">
                        {selectedFile.name}
                      </Text>
                    )}

                    <Button
                      w={{ base: '100%', md: 'auto' }}
                      size={{ base: 'sm', md: 'md' }}
                      colorScheme="green"
                      onClick={() => {
                        onUpload?.(selectedFile);
                        setSelectedFile(null);
                        fileInputRef.current.value = '';
                        // window.location.reload();
                      }}
                    >
                      Submit
                    </Button>

                    <Button
                      w={{ base: '100%', md: 'auto' }}
                      size={{ base: 'sm', md: 'md' }}
                      colorScheme="red"
                      variant="outline"
                      onClick={() => {
                        setSelectedFile(null);
                        fileInputRef.current.value = '';
                      }}
                    >
                      Cancel
                    </Button>
                  </Flex>
                )}
              </>
            )}

            {/* FILTER */}
            {showFilter && (
              <Box w="230px">
                <StyledDropdown
                  options={filterOptions}
                  value={filterValue}
                  placeholder="Masters"
                  bg={bgCard}
                  color={textColor}
                  borderColor={borderColor}
                  onChange={async (val) => {
                    try {
                      const res = await downloadMasterExcel(val);
                      downloadFile(res.data, val);
                    } catch (e) {
                      console.error(e);
                    }
                  }}
                />
              </Box>
            )}
            {errorLogData?.length > 0 && (
              <Box>
                <IconButton
                  icon={<FiHelpCircle />}
                  aria-label="Help"
                  color="red.600"
                  borderRadius="full"
                  animation={`${blink} 1s infinite`}
                  _hover={{ bg: 'red.200' }}
                  onClick={onOpen}
                />
              </Box>
            )}
          </Flex>
        )}
      </Flex>

      {/* DATE FILTER */}
      {showDateFilter && (
        <TableDateFilter
          config={dateFilterConfig}
          values={dateFilterValues}
          onChange={onDateFilterChange}
        />
      )}

      {/* VIEW TOGGLE */}
      {showViewToggle && (
        <Flex justify={{ base: 'center', md: 'flex-end' }} mb={4} gap={2}>
          <IconButton
            aria-label="Table View"
            icon={<FiList size={18} />}
            variant={viewMode === 'table' ? 'solid' : 'outline'}
            colorScheme="blue"
            onClick={() => setViewMode('table')}
          />
          <IconButton
            aria-label="Card View"
            icon={<FiGrid size={18} />}
            variant={viewMode === 'card' ? 'solid' : 'outline'}
            colorScheme="blue"
            onClick={() => setViewMode('card')}
          />
        </Flex>
      )}

      {/* TABLE VIEW */}
      {viewMode === 'table' && (
        <Card
          w="100%"
          bg={bgCard}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="lg"
          p={2}
          boxShadow="rgba(0, 0, 0, 0.35) 0px 5px 15px"
        >
          <Box
            position={'relative'}
            overflowX="auto"
            minHeight="420px"
            maxHeight={{ base: 'auto', md: 'calc(100vh - 280px)' }}
          >
            <Table variant="simple" size="md">
              <Thead bg={headerBg} position="sticky" top={0} zIndex={50}>
                <Tr>
                  {columns.map((col) => (
                    <Th
                      key={col.key}
                      color={headerText}
                      width={col.width}
                      whiteSpace="nowrap"
                      cursor={col.sortable ? 'pointer' : 'default'}
                      onClick={() => col.sortable && onSort?.(col.key)}
                      userSelect="none"
                    >
                      <Flex align="center" gap={1}>
                        {col.label}
                        {col.sortable && (
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            ml={1}
                            gap={0.5}
                          >
                            <ArrowUpIcon
                              boxSize={4}
                              color={
                                sortKey === col.key && sortOrder === 'high'
                                  ? 'green.500'
                                  : 'gray.400'
                              }
                            />
                            <ArrowDownIcon
                              boxSize={4}
                              color={
                                sortKey === col.key && sortOrder === 'low'
                                  ? 'red.500'
                                  : 'gray.400'
                              }
                            />
                          </Box>
                        )}
                      </Flex>
                    </Th>
                  ))}
                  {(onEdit || onToggleActive || onDelete) && (
                    <Th
                      textAlign="center"
                      position="sticky"
                      right={0}
                      zIndex={20}
                      bg={headerBg}
                      boxShadow="rgba(0, 0, 0, 0.1) -4px 0 8px -2px"
                    >
                      Actions
                    </Th>
                  )}
                </Tr>
              </Thead>

              <Tbody>
                {loading
                  ? Array.from({ length: ROWS_PER_PAGE }).map((_, i) => (
                      <Tr key={i}>
                        {columns.map((col, idx) => (
                          <Td key={idx}>
                            <Skeleton height="18px" borderRadius="md" />
                          </Td>
                        ))}
                        {(onEdit || onToggleActive) && (
                          <Td>
                            <Skeleton height="18px" borderRadius="md" />
                          </Td>
                        )}
                      </Tr>
                    ))
                  : data?.map((row, index) => (
                      <MotionTr
                        key={row._id || index}
                        bg={
                          row.isActive === false
                            ? inactiveBg
                            : index % 2 === 0
                              ? zebraBg
                              : 'transparent'
                        }
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => onRowClick?.(row)}
                      >
                        {columns.map((col) => (
                          <Td
                            key={col.key}
                            width={col.width}
                            maxW={{ base: '500px', md: 'unset' }}
                          >
                            {col.render ? (
                              col.render(row[col.key], row)
                            ) : col.key === 'isActive' ? (
                              <Badge
                                colorScheme={row.isActive ? 'green' : 'red'}
                              >
                                {row.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            ) : col.key === 'link' ? (
                              <Tooltip
                                label={row.link}
                                hasArrow
                                placement="top"
                              >
                                <Link
                                  href={row.link}
                                  isExternal
                                  _hover={{ textDecoration: 'none' }}
                                >
                                  <Flex
                                    align="center"
                                    gap={2}
                                    color="teal.500"
                                    fontWeight="500"
                                    fontSize="sm"
                                    _hover={{ color: 'teal.600' }}
                                  >
                                    <ExternalLinkIcon boxSize={3} />
                                    <Text as="span" noOfLines={1} maxW="500px">
                                      {row.link}
                                    </Text>
                                  </Flex>
                                </Link>
                              </Tooltip>
                            ) : col.key === 'pdf' ? (
                              row.pdf ? (
                                <Tooltip label="View Attachment" hasArrow>
                                  <AttachmentIcon
                                    boxSize={5}
                                    cursor="pointer"
                                    color="teal.500"
                                    onClick={() =>
                                      window.open(
                                        `${baseUrl}/getFiles/${row.pdf}`,
                                        '_blank',
                                      )
                                    }
                                  />
                                </Tooltip>
                              ) : (
                                '-'
                              )
                            ) : (
                              <ReadMoreCell
                                email={col.key === 'email'}
                                text={String(row[col.key] || '')}
                              />
                            )}
                          </Td>
                        ))}

                        {(onEdit || onToggleActive || onDelete) && (
                          <Td
                            onClick={(e) => e.stopPropagation()}
                            position="sticky"
                            right={0}
                            zIndex={15}
                            bg={
                              row.isActive === false
                                ? inactiveBg
                                : index % 2 === 0
                                  ? zebraBg
                                  : 'white'
                            }
                            _dark={{
                              bg:
                                row.isActive === false
                                  ? inactiveBg
                                  : index % 2 === 0
                                    ? zebraBg
                                    : 'gray.900',
                            }}
                            boxShadow="rgba(0, 0, 0, 0.1) -4px 0 8px -2px"
                          >
                            <Flex justify="center" gap={5}>
                              {onEdit && (
                                <IconButton
                                  icon={<EditIcon />}
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() => onEdit(row)}
                                />
                              )}
                              {onDelete && (
                                <IconButton
                                  icon={<DeleteIcon />}
                                  size="sm"
                                  colorScheme="red"
                                  onClick={() => onDelete(row)}
                                />
                              )}
                              {onToggleActive && (
                                <Switch
                                  isChecked={row.isActive}
                                  onChange={() => onToggleActive(row._id)}
                                />
                              )}
                            </Flex>
                          </Td>
                        )}
                      </MotionTr>
                    ))}
              </Tbody>
            </Table>
            {!loading && data?.length === 0 && (
              <Flex
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                align="center"
                justify="center"
                pointerEvents="none"
                bg="rgba(255,255,255,0.85)"
                _dark={{ bg: 'rgba(26,32,44,0.85)' }}
              >
                <Flex direction="column" align="center" color={noDataTextColor}>
                  <Text fontSize="lg" fontWeight="600">
                    No data available
                  </Text>
                </Flex>
              </Flex>
            )}
          </Box>

          {/* PAGINATION */}
          <Flex
            justify="space-between"
            align="center"
            mt={4}
            px={4}
            py={3}
            wrap="wrap"
            gap={3}
            bg="white"
            _dark={{ bg: 'gray.800' }}
            rounded="lg"
            boxShadow="sm"
          >
            <Text fontSize="sm" fontWeight="500" color={paginationTextColor}>
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </Text>

            <Flex gap={2}>
              <Button
                size="sm"
                leftIcon={<ChevronLeftIcon />}
                colorScheme="blue"
                variant="outline"
                isDisabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              >
                Prev
              </Button>

              <Button
                size="sm"
                rightIcon={<ChevronRightIcon />}
                colorScheme="blue"
                variant="solid"
                isDisabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        </Card>
      )}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(6px)" />

        <ModalContent maxH="80vh" borderRadius="2xl" overflow="hidden">
          <ModalHeader bg="teal.500" color="white" fontWeight="semibold">
            📂 Master File Error Logs
          </ModalHeader>

          <ModalCloseButton color="white" />

          {/* Scrollable Body */}
          <ModalBody flex="1" overflowY="auto" bg="gray.50" p={5}>
            <VStack spacing={4} align="stretch">
              {errorLogData?.map((item, index) => (
                <Box
                  key={index}
                  p={4}
                  bg="white"
                  borderRadius="xl"
                  boxShadow="sm"
                  transition="0.2s"
                  _hover={{
                    boxShadow: 'md',
                    transform: 'translateY(-2px)',
                  }}
                >
                  <Text fontWeight="bold" fontSize="md">
                    📄 {item.systemFile}
                  </Text>

                  <Text
                    mt={2}
                    fontSize="sm"
                    color="red.500"
                    fontWeight="medium"
                  >
                    ⚠ {item.message}
                  </Text>

                  <Text mt={2} fontSize="xs" color="gray.500">
                    🕒 {new Date(item.createdAt).toLocaleDateString()}
                  </Text>
                </Box>
              ))}
            </VStack>
          </ModalBody>

          <ModalFooter bg="gray.100">
            <Button onClick={onClose} colorScheme="teal" borderRadius="full">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

ReusableTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  onRowClick: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onToggleActive: PropTypes.func,
  onPageChange: PropTypes.func.isRequired,

  showUpload: PropTypes.bool,
  onUpload: PropTypes.func,
  filterOptions: PropTypes.array,
  filterValue: PropTypes.string,
  onFilterChange: PropTypes.func,

  showDateFilter: PropTypes.bool,
  dateFilterConfig: PropTypes.object,
  dateFilterValues: PropTypes.object,
  onDateFilterChange: PropTypes.func,
};

export default ReusableTable;
